import { adminCoachWorkflowFixture } from "./admin-coach-workflows";
import type { RecordScopeDecisionInput } from "./capabilities";
import type { CoachingRole } from "./roles";
import { getRoleDefinition } from "./roles";
import { canRoleReadReport } from "./sensitive-surfaces";
import type { AppRouteDefinition, RouteScopeKind } from "../routes/route-types";
import { ApiClientError } from "../services/api-client";
import type { CoachingServices } from "../services/coaching-service-provider";
import type { CoachingDeliveryMode, CoachingReportType, ListResponse } from "../services/coaching-dtos";

export interface RecordScopeFixture {
  companies: Record<string, { programIds: string[]; cohortIds: string[] }>;
  programs: Record<string, { companyId: string; cohortIds: string[] }>;
  cohorts: Record<string, { companyId: string; programId: string; sessionIds: string[]; participantIds: string[] }>;
  participants: Record<string, { cohortIds: string[] }>;
  sessions: Record<string, { cohortId: string; reportIds: string[] }>;
  // Phase 4: report fixtures carry report_type/delivery so the record-scope
  // resolver can apply the D-3 predicate (direct-URL privacy: a confidential
  // report id resolves to generic not-found for a non-staff role).
  reports: Record<string, { sessionId: string; reportType?: CoachingReportType; deliveryMode?: CoachingDeliveryMode }>;
  resources: Record<string, { global: boolean }>;
  users: Record<string, { role: CoachingRole }>;
}

export const recordScopeFixture: RecordScopeFixture = {
  companies: {
    "company-1": { programIds: ["program-1"], cohortIds: ["cohort-1", "cohort-2"] },
    "company-2": { programIds: ["program-2"], cohortIds: ["cohort-3"] }
  },
  programs: {
    "program-1": { companyId: "company-1", cohortIds: ["cohort-1", "cohort-2"] },
    "program-2": { companyId: "company-2", cohortIds: ["cohort-3"] }
  },
  cohorts: {
    "cohort-1": { companyId: "company-1", programId: "program-1", sessionIds: ["session-1"], participantIds: ["participant-1", "participant-2"] },
    "cohort-2": { companyId: "company-1", programId: "program-1", sessionIds: ["session-2"], participantIds: [] },
    "cohort-3": { companyId: "company-2", programId: "program-2", sessionIds: ["session-3"], participantIds: ["participant-3"] }
  },
  participants: {
    "participant-1": { cohortIds: ["cohort-1"] },
    "participant-2": { cohortIds: ["cohort-1"] },
    "participant-3": { cohortIds: ["cohort-3"] }
  },
  sessions: {
    // Phase 4: each report_type is seeded on session-1 so the D-3 leak spec can
    // prove the FE report-type filter (NOT just record scope) drops the
    // confidential/coach/internal rows for company_admin.
    "session-1": { cohortId: "cohort-1", reportIds: ["report-1", "report-coach-1", "report-internal-1", "report-ceo-1"] },
    "session-2": { cohortId: "cohort-2", reportIds: [] },
    "session-3": { cohortId: "cohort-3", reportIds: ["report-3"] }
  },
  reports: {
    "report-1": { sessionId: "session-1", reportType: "team_admin" },
    "report-coach-1": { sessionId: "session-1", reportType: "coach_feedback" },
    "report-internal-1": { sessionId: "session-1", reportType: "vjal_internal" },
    "report-ceo-1": { sessionId: "session-1", reportType: "ceo_confidential", deliveryMode: "ceo_out_of_band" },
    "report-3": { sessionId: "session-3", reportType: "team_admin" }
  },
  resources: {
    "resource-1": { global: true }
  },
  users: {
    "user-1": { role: "coach" }
  }
};

export function resolveRecordScope(
  role: CoachingRole,
  route: AppRouteDefinition,
  pathname: string,
  fixture: RecordScopeFixture = recordScopeFixture
): RecordScopeDecisionInput {
  if (!route.scopeKind || route.scopeKind === "none") return { status: "in-scope" };

  const recordId = getRouteParam(route, pathname);
  if (!recordId) return unknownScope(route.scopeKind);

  const entity = findScopedEntity(route.scopeKind, recordId, fixture);
  if (!entity) return unknownScope(route.scopeKind);

  if (role === "super_admin" || role === "admin") return { status: "in-scope" };
  if (route.scopeKind === "resource" && fixture.resources[recordId]?.global) return { status: "in-scope" };

  const authorized = isRoleAuthorizedForEntity(role, route.scopeKind, recordId, fixture);
  if (authorized) return { status: "in-scope" };

  return {
    status: "out-of-scope",
    reason: `Record scope for this ${route.scopeKind} route did not authorize rendering; use generic not-found for direct URL privacy.`
  };
}

export function getRouteParam(route: AppRouteDefinition, pathname: string) {
  if (!route.scopeParam) return null;
  const templateParts = route.path.split("/").filter(Boolean);
  const pathParts = pathname.split("?")[0].split("/").filter(Boolean);
  const paramIndex = templateParts.findIndex((part) => part === `:${route.scopeParam}`);
  return paramIndex >= 0 ? pathParts[paramIndex] ?? null : null;
}

export async function resolveRecordScopeFromServices(
  role: CoachingRole,
  route: AppRouteDefinition,
  pathname: string,
  services: CoachingServices
): Promise<RecordScopeDecisionInput> {
  if (!route.scopeKind || route.scopeKind === "none") return { status: "in-scope" };

  const recordId = getRouteParam(route, pathname);
  if (!recordId) return liveUnknownScope(route.scopeKind);

  try {
    const resolved = await fetchScopedRecord(route.scopeKind, recordId, services);
    if (!resolved) return liveUnknownScope(route.scopeKind);
    return {
      status: "in-scope",
      reason: `Live API resolved this ${route.scopeKind} record for ${getRoleDefinition(role).label}.`
    };
  } catch (error) {
    return mapLiveResolverError(route.scopeKind, error);
  }
}

function unknownScope(scopeKind: RouteScopeKind): RecordScopeDecisionInput {
  return {
    status: "unknown",
    reason: `Record scope for this ${scopeKind} route has not been resolved by the catalog-backed resolver; do not render record data.`
  };
}

function liveUnknownScope(scopeKind: RouteScopeKind): RecordScopeDecisionInput {
  return {
    status: "unknown",
    reason: `Record scope for this ${scopeKind} route was not resolved by the live API; do not render record data.`
  };
}

async function fetchScopedRecord(scopeKind: RouteScopeKind, recordId: string, services: CoachingServices) {
  if (scopeKind === "company") return services.companies.detail(recordId);
  if (scopeKind === "program") return services.programs.detail(recordId);
  if (scopeKind === "cohort") return services.cohorts.detail(recordId);
  if (scopeKind === "participant") return findById(await services.participants.list({ id: recordId, include_removed: "true" }), recordId);
  if (scopeKind === "session") return services.sessions.detail(recordId);
  if (scopeKind === "report") return findById(await services.reports.list({ id: recordId }), recordId);
  if (scopeKind === "resource") return services.resources.detail(recordId);
  if (scopeKind === "user") return services.users.detail(recordId);
  return null;
}

function findById<T extends { id: string }>(response: ListResponse<T>, recordId: string) {
  return response.items.find((item) => item.id === recordId) ?? null;
}

function mapLiveResolverError(scopeKind: RouteScopeKind, error: unknown): RecordScopeDecisionInput {
  if (error instanceof ApiClientError) {
    const reference = error.reference ? ` Support reference: ${error.reference}.` : "";
    if (error.status === 403) {
      return {
        status: "out-of-scope",
        reason: `Record scope for this ${scopeKind} route did not authorize rendering; use generic not-found for direct URL privacy.${reference}`
      };
    }
    return {
      status: "unknown",
      reason: `Record scope for this ${scopeKind} route was not resolved by the live API; do not render record data.${reference}`
    };
  }

  return liveUnknownScope(scopeKind);
}

function findScopedEntity(scopeKind: RouteScopeKind, recordId: string, fixture: RecordScopeFixture) {
  if (scopeKind === "company") return fixture.companies[recordId];
  if (scopeKind === "program") return fixture.programs[recordId];
  if (scopeKind === "cohort") return fixture.cohorts[recordId];
  if (scopeKind === "participant") return fixture.participants[recordId];
  if (scopeKind === "session") return fixture.sessions[recordId];
  if (scopeKind === "report") return fixture.reports[recordId];
  if (scopeKind === "resource") return fixture.resources[recordId];
  if (scopeKind === "user") return fixture.users[recordId];
  return null;
}

function isRoleAuthorizedForEntity(role: CoachingRole, scopeKind: RouteScopeKind, recordId: string, fixture: RecordScopeFixture) {
  const cohortIds = allowedCohortIds(role);
  if (scopeKind === "company") return allowedCompanyIds(role, fixture).has(recordId);
  if (scopeKind === "program") return allowedCompanyIds(role, fixture).has(fixture.programs[recordId]?.companyId);
  if (scopeKind === "cohort") return cohortIds.has(recordId);
  if (scopeKind === "participant") return fixture.participants[recordId]?.cohortIds.some((cohortId) => cohortIds.has(cohortId)) ?? false;
  if (scopeKind === "session") return cohortIds.has(fixture.sessions[recordId]?.cohortId);
  if (scopeKind === "report") {
    const report = fixture.reports[recordId];
    if (!cohortIds.has(fixture.sessions[report?.sessionId]?.cohortId)) return false;
    // D-3 direct-URL privacy: a confidential/coach/internal report resolves
    // out-of-scope (generic not-found) for a non-staff role, never the body. A
    // team_admin / legacy report stays scope-governed so a participant keeps
    // own-report access and a company_admin keeps team-report access.
    return canRoleReadReport(role, {
      reportType: report?.reportType === "team_admin" ? undefined : report?.reportType,
      deliveryMode: report?.deliveryMode
    });
  }
  if (scopeKind === "user") return role === "super_admin" || role === "admin";
  return false;
}

function allowedCohortIds(role: CoachingRole) {
  if (role === "coach") {
    return new Set(adminCoachWorkflowFixture.cohorts.filter((cohort) => cohort.coachId === "coach-1").map((cohort) => cohort.id));
  }
  if (role === "company_admin") return new Set(["cohort-1", "cohort-2"]);
  if (role === "participant") return new Set(["cohort-1"]);
  return new Set<string>();
}

function allowedCompanyIds(role: CoachingRole, fixture: RecordScopeFixture) {
  if (role === "company_admin") return new Set(["company-1"]);
  const cohortIds = allowedCohortIds(role);
  return new Set(
    Object.entries(fixture.cohorts)
      .filter(([cohortId]) => cohortIds.has(cohortId))
      .map(([, cohort]) => cohort.companyId)
  );
}
